import { createAsyncThunk } from '@reduxjs/toolkit';
import createGenericSlice, { GenericState } from '../wrapper/sliceWrapper';
import IUserList from '../../utils/UserList/UserList.interface';
import OrganizationAdmin from '../../utils/OrganizationAdmin/OrganizationAdmin';
import AllOrganizationAdmin from '../../utils/OrganizationUser/AllOrganisationUser';
import OrgUser from '../../utils/OrgUser/OrgUser';

interface IActionType {
  payload: IUserList;
  type: string;
}

interface ISwitchAdminAction {
  payload: { oldEmail: string; updatedPartner: IUserList };
  type: string;
}

interface IUserDetails {
  email: string;
  orgEmail: string;
  orgName: string;
  name: string;
  role: string;
  phone: string;
}

const initialState: GenericState<Array<IUserList> | null> = { data: null, status: 'loading' };
// get all partner organizations
export const getAllAdmins = createAsyncThunk('partners/getAllPartnerOrgs', async () => {
  const organizationAdminObj = new OrganizationAdmin();
  return await organizationAdminObj.getAllOrganization();
});
// get partner users by admin email - for later use
export const getPartnerUsers = createAsyncThunk(
  'partners/getPartnerUser',
  async (email: string) => {
    const organizationAdminObj = new AllOrganizationAdmin({ email });
    return await organizationAdminObj.getAllAdminUser();
  }
);
// Add user to and org - for later use
export const addPartnerUser = createAsyncThunk(
  'partners/addPartnerUser',
  async (user: IUserDetails, thunkAPI) => {
    const userObj = new OrgUser(user);
    const createStatus = await userObj.createUser();
    if (createStatus.statusCode === 201) {
      return user;
    }
    return thunkAPI.rejectWithValue(createStatus);
  }
);
// Delete Partner user - for later use
export const deletePartnerUser = createAsyncThunk(
  'partners/deletePartnerUser',
  async (user: IUserDetails, thunkAPI) => {
    const userObj = new OrgUser(user);
    const createStatus = await userObj.createUser();
    if (createStatus.statusCode === 201) {
      return user;
    }
    return thunkAPI.rejectWithValue(createStatus);
  }
);

const partnerSlice = createGenericSlice({
  name: 'partners',
  initialState,
  reducers: {
    // On deletion of partner organization
    deletePartner: (state, action: IActionType) => {
      state.data = state.data?.filter((item) => item.email !== action.payload.email) ?? state.data;
    },
    // On change of partner details
    changePartner: (state, action: IActionType) => {
      state.data =
        state.data?.map((item) => (item.email === action.payload.email ? action.payload : item)) ??
        state.data;
    },
    // On switching org admin — matches by old email since email changes
    switchAdmin: (state, action: ISwitchAdminAction) => {
      const { oldEmail, updatedPartner } = action.payload;
      state.data =
        state.data?.map((item) => (item.email === oldEmail ? updatedPartner : item)) ?? state.data;
    },
    // Reset to initial state
    resetPartnerState: (state) => {
      state = initialState;
    }
  },
  extraReducers: (builder) => {
    // getAllAdmins cases
    builder.addCase(getAllAdmins.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(getAllAdmins.fulfilled, (state, action) => {
      state.status = 'finished';
      state.data = action.payload;
    });

    builder.addCase(getAllAdmins.rejected, (state, action) => {
      state.status = 'error';
      state.error = 'Something went wrong.';
    });
  }
});

export const { deletePartner, changePartner, switchAdmin, resetPartnerState } = partnerSlice.actions;
export default partnerSlice.reducer;
